import React, { useState, useCallback } from "react";
import { MdSearch, MdClear } from "react-icons/md";

interface MenuSearchProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  placeholder?: string;
  className?: string; // Allow external styling
}

const MenuSearch: React.FC<MenuSearchProps> = ({
  searchQuery,
  onSearchChange,
  placeholder = "Search menu items, ingredients, or categories...",
  className = "",
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value);
    },
    [onSearchChange]
  );

  const handleClear = useCallback(() => {
    onSearchChange("");
  }, [onSearchChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        handleClear();
        (e.target as HTMLInputElement).blur();
      }
    },
    [handleClear]
  );

  return (
    <div className={`relative w-full ${className}`}>
      <div className="relative flex items-center transition-all duration-200">
        {/* Search Icon */}
        <div className="absolute left-3 z-10 pointer-events-none">
          <MdSearch
            className={`w-5 h-5 transition-colors duration-200 ${
              isFocused ? "text-red-500" : "text-gray-400"
            }`}
          />
        </div>

        {/* Search Input */}
        <input
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Search menu"
          className={`w-full pl-10 pr-10 py-2 sm:py-3 text-sm sm:text-base rounded-lg border bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-100 ${
            isFocused
              ? "border-red-500 shadow-md"
              : "border-gray-300 hover:border-gray-400"
          }`}
        />

        {/* Clear Button */}
        {searchQuery && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute right-3 z-10 p-1 rounded-full hover:bg-gray-100 transition-colors duration-200"
          >
            <MdClear className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>
    </div>
  );
};

export default MenuSearch;
